"use client";

import React, {useEffect, useState} from "react";
import Cookies from "js-cookie";
import {Download, FileText, Loader2} from "lucide-react";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Alert, AlertDescription} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";

export interface AttachmentResponse {
    cprownum: string;
    name: string;
    type: string;
    size: string;
    path_allegato: string;
}

interface CarAllegatiProps {
    codiceArticolo: string;
    authToken?: string | null;
}

type FetchStatus = "idle" | "loading" | "success" | "empty" | "error";

const handleDownload = (url: string, fileName: string) => {
    try {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error("Errore nel download dell'allegato:", error);
    }
};

const CarAllegati: React.FC<CarAllegatiProps> = ({codiceArticolo, authToken}) => {
    const [attachments, setAttachments] = useState<AttachmentResponse[]>([]);
    const [status, setStatus] = useState<FetchStatus>("idle");

    useEffect(() => {
        if (!codiceArticolo) {
            return;
        }

        const effectiveAuthToken = authToken ?? Cookies.get("authToken");
        if (!effectiveAuthToken) {
            setStatus("empty");
            return;
        }

        const controller = new AbortController();

        const fetchAttachments = async () => {
            try {
                setStatus("loading");
                const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/dashboard/elenco-allegati?codiceArticolo=${encodeURIComponent(codiceArticolo)}&foto=0`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${effectiveAuthToken}`,
                        "Content-Type": "application/json",
                    },
                    signal: controller.signal,
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error(`Unable to load attachments (${response.status})`);
                }

                const data = await response.json();
                const parsed = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

                if (!parsed || parsed.length === 0) {
                    setStatus("empty");
                    setAttachments([]);
                    return;
                }

                setAttachments(parsed as AttachmentResponse[]);
                setStatus("success");
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }
                console.error("Errore durante il recupero degli allegati del veicolo:", error);
                setStatus("error");
            }
        };

        fetchAttachments();

        return () => {
            controller.abort();
        };
    }, [authToken, codiceArticolo]);

    const attachmentsCount = attachments.length;

    if (status === "loading") {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Allegati
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Caricamento allegati…
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (status === "error") {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Allegati
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Alert variant="destructive">
                        <AlertDescription>
                            Errore nel caricamento degli allegati. Riprova più tardi.
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    if (status === "empty" || attachmentsCount === 0) {
        return null;
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Allegati
                        <span className="text-sm text-muted-foreground">({attachmentsCount})</span>
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/40">
                                <TableHead className="font-medium">Nome File</TableHead>
                                <TableHead className="font-medium">Tipo</TableHead>
                                <TableHead className="font-medium">Dimensione</TableHead>
                                <TableHead className="font-medium w-20 text-right">Azioni</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {attachments.map((file) => (
                                <TableRow key={file.cprownum}>
                                    <TableCell className="font-medium">{file.name}</TableCell>
                                    <TableCell>
                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs uppercase">
                                            {file.type || "N/D"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">{file.size || "-"}</TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDownload(file.path_allegato, file.name)}
                                            className="hover:bg-blue-50"
                                            title={`Scarica ${file.name}`}
                                        >
                                            <Download className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default CarAllegati;
